import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';

import { routeAnimations } from '../../../animations';
import { SiteState } from '../../../shared/models/shared.model';
import { Contactperson } from '../../models/contactperson.model';
import {
  setSelectedContactpersonId,
  clearSelectedContactperson,
} from '../../store/contactperson.actions';
import { getSelectedContactperson } from '../../store/contactperson.selectors';

@Component({
  selector: 'app-contactperson-item',
  templateUrl: './contactperson-item.component.html',
  animations: [routeAnimations],
  standalone: false,
})
export class ContactpersonItemComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly routeAnimationState = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.route.firstChild?.routeConfig?.data ?? false),
    ),
    { initialValue: this.route.firstChild?.routeConfig?.data ?? false },
  );

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getUrl));

  pageTitle$: Observable<string> = this.store.pipe(
    select(getSelectedContactperson),
    withLatestFrom(this.routeUrl$),
    map(([contactperson, url]: [Contactperson, string]) => {
      const isAdd = url === '/contactperson/add';
      const title = this.locale.translate(
        `app.contactperson.${isAdd ? 'add' : 'edit'}-contactperson`,
      );
      const name =
        !!contactperson && Object.hasOwnProperty.call(contactperson, 'firstName')
          ? `${contactperson.firstName} ${contactperson.lastName}`
          : '';
      return `${title} ${isAdd ? '' : name}`;
    }),
  );

  ngOnInit(): void {
    const contactpersonId: number = parseInt(
      this.route.snapshot.params['contactpersonId'] as string,
      10,
    );

    if (contactpersonId) {
      this.store.dispatch(setSelectedContactpersonId({ contactpersonId }));
    } else {
      this.store.dispatch(clearSelectedContactperson());
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearSelectedContactperson());
  }

  back = () => this.router.navigate(['/contactperson']);
}
